/**
 * McpConfigExamples - Configuration examples for AI assistants
 *
 * Features:
 * - Tabbed examples for Claude Desktop, Cursor, Custom HTTP
 * - Copy-to-clipboard for each config
 * - Placeholder for user's MCP key
 */

import React, { useState, useCallback } from 'react';
import { Button, Tabs, TabsList, TabsTrigger, TabsContent, Badge } from '@/components/ui';
import { Copy, Check, ExternalLink, Terminal, Monitor, Code } from 'lucide-react';
import { MCP_SERVER_URL } from '@/config/env';

interface McpConfigExamplesProps {
  organizationId?: string;
}

export function McpConfigExamples({ organizationId }: McpConfigExamplesProps) {
  const [copiedTab, setCopiedTab] = useState<string | null>(null);

  const handleCopy = useCallback(async (content: string, tabId: string) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedTab(tabId);
      setTimeout(() => setCopiedTab(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }, []);

  // Claude Desktop / Claude Code config (Streamable HTTP transport - MCP 2025-03-26 spec)
  const claudeDesktopConfig = `{
  "mcpServers": {
    "simcheck": {
      "type": "http",
      "url": "${MCP_SERVER_URL}/mcp",
      "headers": {
        "Authorization": "Bearer sk_mcp_YOUR_KEY_HERE"
      }
    }
  }
}`;

  // Cursor config (same format as Claude)
  const cursorConfig = `{
  "mcpServers": {
    "simcheck": {
      "type": "http",
      "url": "${MCP_SERVER_URL}/mcp",
      "headers": {
        "Authorization": "Bearer sk_mcp_YOUR_KEY_HERE"
      }
    }
  }
}`;

  // cURL example for chat API
  const curlExample = `curl -X POST "${MCP_SERVER_URL}/api/chat" \\
  -H "Authorization: Bearer sk_mcp_YOUR_KEY_HERE" \\
  -H "Content-Type: application/json" \\
  -d '{"message": "List my test flows"}'`;

  // cURL example for MCP protocol
  const mcpCurlExample = `# Initialize MCP session
curl -X POST "${MCP_SERVER_URL}/mcp" \\
  -H "Authorization: Bearer sk_mcp_YOUR_KEY_HERE" \\
  -H "Content-Type: application/json" \\
  -H "Accept: application/json, text/event-stream" \\
  -d '{"jsonrpc":"2.0","id":1,"method":"initialize","params":{}}'`;

  return (
    <Tabs defaultValue="claude" className="w-full">
      <TabsList className="grid w-full grid-cols-3">
        <TabsTrigger value="claude" className="flex items-center gap-2">
          <Monitor className="w-4 h-4" />
          <span className="hidden sm:inline">Claude Desktop</span>
          <span className="sm:hidden">Claude</span>
        </TabsTrigger>
        <TabsTrigger value="cursor" className="flex items-center gap-2">
          <Code className="w-4 h-4" />
          Cursor
        </TabsTrigger>
        <TabsTrigger value="custom" className="flex items-center gap-2">
          <Terminal className="w-4 h-4" />
          <span className="hidden sm:inline">Custom HTTP</span>
          <span className="sm:hidden">HTTP</span>
        </TabsTrigger>
      </TabsList>

      {/* Claude Desktop Tab */}
      <TabsContent value="claude" className="space-y-4">
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h4 className="font-medium">Claude Desktop Configuration</h4>
            <a
              href="https://claude.ai/download"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-primary hover:underline flex items-center gap-1"
            >
              Download Claude Desktop
              <ExternalLink className="w-3 h-3" />
            </a>
          </div>
          <p className="text-sm text-muted-foreground">
            Add this to your <code className="bg-muted px-1 py-0.5 rounded text-xs">claude_desktop_config.json</code> file
          </p>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Badge variant="secondary">macOS</Badge>
            <span className="text-xs text-muted-foreground font-mono">
              ~/Library/Application Support/Claude/claude_desktop_config.json
            </span>
          </div>
          <div className="flex items-center justify-between">
            <Badge variant="secondary">Windows</Badge>
            <span className="text-xs text-muted-foreground font-mono">
              %APPDATA%\Claude\claude_desktop_config.json
            </span>
          </div>
        </div>

        <div className="relative">
          <pre className="bg-muted p-4 rounded-lg overflow-x-auto text-sm font-mono">
            {claudeDesktopConfig}
          </pre>
          <Button
            variant="outline"
            size="sm"
            className="absolute top-2 right-2"
            onClick={() => handleCopy(claudeDesktopConfig, 'claude')}
          >
            {copiedTab === 'claude' ? (
              <>
                <Check className="w-4 h-4 mr-1 text-green-500" />
                Copied
              </>
            ) : (
              <>
                <Copy className="w-4 h-4 mr-1" />
                Copy
              </>
            )}
          </Button>
        </div>

        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
          <p className="text-sm text-amber-600 dark:text-amber-400">
            <strong>Note:</strong> Replace <code className="bg-muted px-1 py-0.5 rounded text-xs">sk_mcp_YOUR_KEY_HERE</code> with your actual MCP key. Restart Claude Desktop after making changes.
          </p>
        </div>
      </TabsContent>

      {/* Cursor Tab */}
      <TabsContent value="cursor" className="space-y-4">
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h4 className="font-medium">Cursor Configuration</h4>
            <a
              href="https://cursor.sh"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-primary hover:underline flex items-center gap-1"
            >
              Get Cursor
              <ExternalLink className="w-3 h-3" />
            </a>
          </div>
          <p className="text-sm text-muted-foreground">
            Add this to your project's <code className="bg-muted px-1 py-0.5 rounded text-xs">.cursor/mcp.json</code> file
          </p>
        </div>

        <div className="relative">
          <pre className="bg-muted p-4 rounded-lg overflow-x-auto text-sm font-mono">
            {cursorConfig}
          </pre>
          <Button
            variant="outline"
            size="sm"
            className="absolute top-2 right-2"
            onClick={() => handleCopy(cursorConfig, 'cursor')}
          >
            {copiedTab === 'cursor' ? (
              <>
                <Check className="w-4 h-4 mr-1 text-green-500" />
                Copied
              </>
            ) : (
              <>
                <Copy className="w-4 h-4 mr-1" />
                Copy
              </>
            )}
          </Button>
        </div>

        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
          <p className="text-sm text-amber-600 dark:text-amber-400">
            <strong>Note:</strong> Replace <code className="bg-muted px-1 py-0.5 rounded text-xs">sk_mcp_YOUR_KEY_HERE</code> with your actual MCP key.
          </p>
        </div>
      </TabsContent>

      {/* Custom HTTP Tab */}
      <TabsContent value="custom" className="space-y-4">
        <div className="space-y-2">
          <h4 className="font-medium">Custom HTTP Integration</h4>
          <p className="text-sm text-muted-foreground">
            Use these endpoints to build custom integrations with any HTTP client
          </p>
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Badge>MCP</Badge>
            <code className="text-xs bg-muted px-2 py-1 rounded font-mono">{MCP_SERVER_URL}/mcp</code>
          </div>
          <p className="text-xs text-muted-foreground pl-14">
            MCP Streamable HTTP endpoint (GET for SSE, POST for JSON-RPC)
          </p>

          <div className="flex items-center gap-2 mt-4">
            <Badge>POST</Badge>
            <code className="text-xs bg-muted px-2 py-1 rounded font-mono">{MCP_SERVER_URL}/api/chat</code>
          </div>
          <p className="text-xs text-muted-foreground pl-14">
            Natural language chat endpoint (REST API)
          </p>
        </div>

        <div className="space-y-2">
          <h5 className="text-sm font-medium">Chat API Example</h5>
          <div className="relative">
            <pre className="bg-muted p-4 rounded-lg overflow-x-auto text-sm font-mono whitespace-pre-wrap">
              {curlExample}
            </pre>
            <Button
              variant="outline"
              size="sm"
              className="absolute top-2 right-2"
              onClick={() => handleCopy(curlExample, 'custom-chat')}
            >
              {copiedTab === 'custom-chat' ? (
                <>
                  <Check className="w-4 h-4 mr-1 text-green-500" />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4 mr-1" />
                  Copy
                </>
              )}
            </Button>
          </div>
        </div>

        <div className="space-y-2">
          <h5 className="text-sm font-medium">MCP Protocol Example</h5>
          <div className="relative">
            <pre className="bg-muted p-4 rounded-lg overflow-x-auto text-sm font-mono whitespace-pre-wrap">
              {mcpCurlExample}
            </pre>
            <Button
              variant="outline"
              size="sm"
              className="absolute top-2 right-2"
              onClick={() => handleCopy(mcpCurlExample, 'custom-mcp')}
            >
              {copiedTab === 'custom-mcp' ? (
                <>
                  <Check className="w-4 h-4 mr-1 text-green-500" />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4 mr-1" />
                  Copy
                </>
              )}
            </Button>
          </div>
        </div>

        <div className="bg-muted/50 border rounded-lg p-4 space-y-2">
          <h5 className="font-medium text-sm">Authentication Header</h5>
          <code className="text-xs bg-background px-2 py-1 rounded font-mono block">
            Authorization: Bearer sk_mcp_YOUR_KEY_HERE
          </code>
        </div>
      </TabsContent>
    </Tabs>
  );
}

export default McpConfigExamples;
